import React, { useState } from "react";
import PropTypes from "prop-types";
import pluralize from "pluralize";
import LcFetch from "lib/shared/fetch";
import QuizCard from "./QuizCard/QuizCard";
import { QUIZ_ANSWER_STATES } from "./constants";

const propTypes = {
  attemptCount: PropTypes.number.isRequired,
  isAnonUser: PropTypes.bool.isRequired,
  lastQuizSubmissionSummary: PropTypes.shape({
    correct: PropTypes.number,
    incorrect: PropTypes.number,
    percentCorrect: PropTypes.number,
    reviewLink: PropTypes.string,
    skipped: PropTypes.number
  }),
  locked: PropTypes.bool.isRequired,
  lockedRedirectUrl: PropTypes.string,
  quizQuestions: PropTypes.arrayOf(
    PropTypes.shape({
      answerOptions: PropTypes.arrayOf(
        PropTypes.shape({
          answerLetter: PropTypes.string.isRequired,
          answerText: PropTypes.string.isRequired,
          state: PropTypes.oneOf([
            QUIZ_ANSWER_STATES.UNLOCKED,
            QUIZ_ANSWER_STATES.LOCKED
          ])
        })
      ),
      id: PropTypes.number.isRequired,
      question: PropTypes.string.isRequired
    })
  ).isRequired,
  quizTitle: PropTypes.string.isRequired,
  submissionUrl: PropTypes.string.isRequired
};

const defaultProps = {
  lastQuizSubmissionSummary: null,
  lockedRedirectUrl: null
};

function QuizSubmission({
  attemptCount,
  lastQuizSubmissionSummary,
  locked,
  lockedRedirectUrl,
  quizQuestions,
  quizTitle,
  isAnonUser,
  submissionUrl
}) {
  const [isLoading, setIsLoading] = useState(false);
  const emptyQuestionsSubmission = quizQuestions.reduce(
    (acc, cur) => Object.assign(acc, { [cur.id]: null }),
    {}
  );
  const [quizQuestionsSubmission, setQuizQuestionsSubmission] = useState(
    emptyQuestionsSubmission
  );

  const handleLockedState = () => {
    window.location.href = lockedRedirectUrl;
  };

  const handleSubmit = () => {
    if (locked) {
      // TODO: Future story to paywall, for now, redirect
      handleLockedState();
      return;
    }

    const token = document.querySelector("meta[name='csrf-token']").content;
    const reloadPath = window.location.href.split("?")[0];
    const request = {
      body: JSON.stringify({
        quiz_submission: {
          quiz_question_submissions_attributes: Object.keys(
            quizQuestionsSubmission
          ).map((key) => {
            return {
              chosen_answer: quizQuestionsSubmission[key],
              quiz_question_id: key
            };
          })
        }
      }),
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token
      },
      method: "POST"
    };

    setIsLoading(true);

    LcFetch(submissionUrl, request)
      .then((_response) => {
        window.location.href = reloadPath;
      })
      .catch((error) => {
        // TODO: Use toast notifications to indicate failure when it's available.
        console.error("Something went wrong.", error);
        setIsLoading(false);
        alert("Something went wrong.");
      });
  };

  const handleAnswerSelect = (chosenAnswer, questionId) => {
    if (locked) {
      handleLockedState();
      return;
    }
    const updatedSubmission = quizQuestionsSubmission;
    updatedSubmission[questionId] = chosenAnswer;
    setQuizQuestionsSubmission(updatedSubmission);
  };

  const amplitudeEvent = locked
    ? "quizLockedSubmitButton"
    : "quizUnlockedSubmitButton";

  const amplitudeFunnel = () => {
    if (!locked) {
      return "none";
    }
    if (isAnonUser) {
      return "free";
    }
    return "paid";
  };

  return (
    <div>
      <div className="summary-section-quiz--description body-2">
        {attemptCount > 0
          ? `You took this quiz ${pluralize(
              "time",
              attemptCount,
              true
            )} before and got ${
              lastQuizSubmissionSummary.percentCorrect
            }% last time. Retake this quiz to see your new results.`
          : `Test your knowledge of ${quizTitle}. Submit your answers to see your results and get feedback.`}
      </div>
      <div className="quiz-questions">
        {quizQuestions.map((quizQuestion, index) => {
          return (
            <QuizCard
              onAnswerSelect={(answerLetter) =>
                handleAnswerSelect(answerLetter, quizQuestion.id)
              }
              key={quizQuestion.id}
              answerOptions={quizQuestion.answerOptions}
              locked={locked}
              isAnonUser={isAnonUser}
              questionId={quizQuestion.id}
              questionIndex={index + 1}
              questionText={quizQuestion.question}
              questionTotal={quizQuestions.length}
            />
          );
        })}
      </div>
      <div className="summary-section-quiz--submit">
        <button
          className="btn btn-a-plus button-1 btn-lg"
          data-amplitude={amplitudeEvent}
          data-amplitude-funnel={amplitudeFunnel()}
          data-testid="QuizSubmitButton"
          disabled={isLoading}
          onClick={isAnonUser ? null : handleSubmit}
          type="button"
          {...(isAnonUser
            ? {
                "data-target": "#quiz-free-tier .modal",
                "data-toggle": "modal"
              }
            : {})}
        >
          {locked && <i className="lc-icon lc-icon-lock-filled lc-icon-sm" />}
          Submit
        </button>
      </div>
    </div>
  );
}

QuizSubmission.propTypes = propTypes;
QuizSubmission.defaultProps = defaultProps;

export default QuizSubmission;
